import { withApollo } from 'react-apollo'

import { ModalActionTypes } from '@acorns/web-components'
import ApolloClient from 'apollo-client'
import { pathOr } from 'ramda'
import { compose, lifecycle, withHandlers, withProps, withState } from 'recompose'

import { PARTNER_PAYLOAD_STORAGE_KEY } from 'app-constants'
import { withApolloErrorModal } from 'utils/Apollo'
import { sendToSentry } from 'utils/errors'
import removeDuplicateQueryParams from 'utils/sourceAttributes'

import { partnerQuery } from './query'

export type Partner = {
  org: {
    authorization: {
      accessToken: string
      expiresAt: string
      provider: string
      refreshToken: string
      uid: string
    }
    id: string
    name: string
    website: string
    externalId: string
    logoTypeUrl: string
    accountExistsRedirectUrl: string
    clientCredentialsToken: string
  }
  user: {
    firstName: string
    lastName: string
    email: string
    dob: string
    address1: string
    address2: string
    city: string
    ssn: string
    state: string
    zip: string
    phoneNumber: string
    usCitizen: boolean
  }
  accountToken: string
  routingNumbers?: PartnerRoutingNumber[]
}

type PartnerRoutingNumber = {
  routingNumber: string
}

type AddedProps = {
  client: ApolloClient<any>
}

type StateProps = {
  partner: Partner
  setPartner: (partnerData: any) => void
  isPartnerReg: boolean
  setIsPartnerReg: (isPartnerReg: boolean) => void
}

type HandlerProps = {
  decrypt: (token: string, partnerToken: string) => Promise<any>
}

type InnerProps = {
  partner: Partner
} & AddedProps &
  StateProps &
  HandlerProps

export type WithPartnerProps = InnerProps

export const formatPartnerData = (data: any): Partner => ({
  accountToken: data.accountToken,
  routingNumbers: data.routingNumbers,
  org: {
    authorization: {
      accessToken: pathOr(null, ['authorization', 'accessToken'], data),
      expiresAt: pathOr(null, ['authorization', 'expiresAt'], data),
      provider: pathOr(null, ['authorization', 'provider'], data),
      uid: pathOr(null, ['authorization', 'uid'], data),
      refreshToken: pathOr(null, ['authorization', 'refreshToken'], data),
    },
    accountExistsRedirectUrl: data.existingAccountRedirectUri,
    clientCredentialsToken: data.clientCredentialsToken,
    externalId: data.externalId,
    id: data.id,
    logoTypeUrl: data.logoTypeUrl,
    name: data.name,
    website: data.website,
  },
  user: {
    address1: data.user.address1 || '',
    address2: data.user.address2 || '',
    city: data.user.city || '',
    dob: data.user.dob || '',
    email: data.user.email || '',
    firstName: data.user.firstName || '',
    lastName: data.user.lastName || '',
    phoneNumber: data.user.phoneNumber || '',
    ssn: data.user.ssn || '',
    state: data.user.state || '',
    zip: data.user.zip || '',
    usCitizen: data.user.usCitizen === undefined ? null : data.user.usCitizen,
  },
})

// Determine if current user is registering through a partner
function getIsPartnerRegInitialState() {
  const partnerData = window.sessionStorage.getItem(PARTNER_PAYLOAD_STORAGE_KEY)
  const query = removeDuplicateQueryParams(location.search)

  if (partnerData || (query.token && query.payload_token)) {
    return true
  } else {
    return false
  }
}

export const formatError = error => {
  const message = pathOr(null, ['graphQLErrors', 0, 'message'], error)
  const extra = message ? ` (Error: ${message})` : ''

  return {
    actions: [
      {
        label: 'Okay',
        handler: () => {
          window.location.assign(window.location.origin)
        },
        type: ModalActionTypes.primary,
      },
    ],
    message: `There was an issue with the provided partner information.${extra}`,
    title: 'Uh-Oh',
  }
}

export default compose<InnerProps, {}>(
  withApollo,
  withApolloErrorModal,
  withState('partner', 'setPartner', null),
  withProps({
    isPartnerReg: getIsPartnerRegInitialState(),
  }),
  withHandlers<InnerProps, HandlerProps>({
    decrypt: ({ client }) => (token, partnerToken) => {
      return client.query({
        query: partnerQuery,
        variables: {
          oauth: token,
          payload: partnerToken,
        },
      })
    },
  }),
  lifecycle<InnerProps, {}, {}>({
    componentWillMount() {
      // Check if query string contains partner tokens
      const query = removeDuplicateQueryParams(location.search)
      if (query.token && query.payload_token) {
        const payloadToken = Array.isArray(query.payload_token)
          ? query.payload_token[0]
          : query.payload_token
        this.props
          .decrypt(query.token, payloadToken)
          .then(response => {
            const partnerData = formatPartnerData(response.data.partner)
            window.sessionStorage.setItem(PARTNER_PAYLOAD_STORAGE_KEY, JSON.stringify(partnerData))
            this.props.setPartner(partnerData)
          })
          .catch(error => {
            sendToSentry(error, {
              name: 'PartnerTokenError',
              message: 'Partner tokens found in URL but could not be resolved',
            })

            this.props.showErrorModal(formatError(error))
          })
      } else {
        // Potentially retrieve data from session storage
        const partnerData = window.sessionStorage.getItem(PARTNER_PAYLOAD_STORAGE_KEY)
        this.props.setPartner(JSON.parse(partnerData))
      }
    },
  }),
)
